import { ref } from 'vue';

/**
 * Composable for managing loading states with VLoader component
 *
 * @returns {Object}                      Loading state utilities
 * @property {Ref<boolean>} isLoading     Reactive reference to loading state
 * @property {Ref<string>} loadingText    Text to display with the loader
 * @property {Ref<boolean>} fullPage      Whether to show a full-page overlay backdrop
 * @property {Ref<boolean>} preventScroll Whether to prevent scrolling when loader is active
 * @property {Function} startLoading      Start loading with optional text, fullPage and preventScroll settings
 * @property {Function} stopLoading       Stop loading
 * @property {Function} withLoading       Wrap an async function with loading state
 */
export function useVLoader() {
    const isLoading = ref(false);
    const loadingText = ref('');
    const fullPage = ref(true);
    const preventScroll = ref(true);

    /**
     * Start loading
     *
     * @param {string} text             Optional text to display with the loader
     * @param {boolean} isFullPage      Whether to show a full-page overlay
     * @param {boolean} isPreventScroll Whether to prevent scrolling when loader is active
     */
    const startLoading = (text = '', isFullPage = true, isPreventScroll = true) => {
        isLoading.value = true;
        loadingText.value = text;
        fullPage.value = isFullPage;
        preventScroll.value = isPreventScroll;
    };

    /**
     * Stop loading
     */
    const stopLoading = () => {
        isLoading.value = false;
        loadingText.value = '';
        fullPage.value = false;
        preventScroll.value = true;
    };

    /**
     * Wrap an async function with loading state
     *
     * @param {Function} fn             Async function to wrap
     * @param {string} text             Optional text to display with the loader
     * @param {boolean} isFullPage      Whether to show a full-page overlay
     * @param {boolean} isPreventScroll Whether to prevent scrolling when loader is active
     * @returns {Promise<any>}
     */
    const withLoading = async (fn, text = '', isFullPage = false, isPreventScroll = true) => {
        try {
            startLoading(text, isFullPage, isPreventScroll);
            return await fn();
        } finally {
            stopLoading();
        }
    };

    return {
        isLoading,
        loadingText,
        fullPage,
        preventScroll,
        startLoading,
        stopLoading,
        withLoading,
    };
}
